import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Link,
} from '@mui/material';
import { Business, Phone, Email, LocationOn } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import api from '../services/api';

const ContactForm = () => {
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // Track form load time for spam detection
  React.useEffect(() => {
    window.formLoadTime = Date.now();
  }, []);

  const onSubmit = useCallback(async (data) => {
    setLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (data.website) {
      setSubmitStatus({ type: 'error', message: 'Invalid submission detected.' });
      setLoading(false);
      return;
    }

    try {
      let recaptchaToken = null;

      // Try to get reCAPTCHA token, but don't fail if unavailable
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('contact_form');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA execution failed, submitting without token:', recaptchaError);
          // Continue with submission even if reCAPTCHA fails
        }
      } else {
        console.warn('reCAPTCHA not available, submitting without token');
      }

      // Call the centralized email service
      const response = await fetch('https://ays-production.up.railway.app/api/contact/paceestate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.full_name,
          email: data.email,
          phone: data.phone,
          vessel: `${data.address || ''} ${data.city || ''} ${data.state || ''} ${data.zip_code || ''}`.trim(),
          service: `${data.service_type || ''} - ${data.property_type || ''} - ${data.timeline || ''}`,
          message: data.message,
          captchaToken: recaptchaToken,
          formLoadTime: window.formLoadTime || Date.now(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit form');
      }

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for contacting us! We will get back to you within 24 hours.',
      });
      reset();
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.error || error.response?.data?.detail || 'Failed to submit form. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, reset]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 6 }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Box textAlign="center" mb={4}>
            <Business sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" component="h1" gutterBottom color="primary" fontWeight="bold">
              PaceEstate
            </Typography>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Professional Estate Sales Services
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Serving Florida with Excellence and Integrity
            </Typography>
          </Box>

          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <Phone sx={{ color: 'secondary.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Call Us
                  </Typography>
                  <Typography variant="h6">(850) 555-PACE</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <Email sx={{ color: 'secondary.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email Us
                  </Typography>
                  <Typography variant="h6">info@paceestate.com</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <LocationOn sx={{ color: 'secondary.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="h6">Florida</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
            Request a Free Consultation
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Fill out the form below and our team will contact you within 24 hours to discuss your estate sale needs.
          </Typography>

          {submitStatus.message && (
            <Alert severity={submitStatus.type} sx={{ mb: 3 }}>
              {submitStatus.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              type="text"
              {...register('website')}
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  {...register('full_name', { required: 'Full name is required' })}
                  error={!!errors.full_name}
                  helperText={errors.full_name?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  {...register('phone', { required: 'Phone number is required' })}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    {...register('service_type', { required: 'Service type is required' })}
                    defaultValue=""
                    label="Service Type"
                    error={!!errors.service_type}
                  >
                    <MenuItem value="estate_sale">Estate Sale</MenuItem>
                    <MenuItem value="downsizing">Downsizing</MenuItem>
                    <MenuItem value="liquidation">Liquidation</MenuItem>
                    <MenuItem value="appraisal">Appraisal</MenuItem>
                    <MenuItem value="consignment">Consignment</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property Address"
                  {...register('address', { required: 'Address is required' })}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  {...register('city', { required: 'City is required' })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  {...register('state')}
                  defaultValue="FL"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  {...register('zip_code', { required: 'ZIP code is required' })}
                  error={!!errors.zip_code}
                  helperText={errors.zip_code?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Property Type</InputLabel>
                  <Select
                    {...register('property_type')}
                    defaultValue=""
                    label="Property Type"
                  >
                    <MenuItem value="single_family">Single Family Home</MenuItem>
                    <MenuItem value="condo">Condo/Townhouse</MenuItem>
                    <MenuItem value="apartment">Apartment</MenuItem>
                    <MenuItem value="estate">Estate/Large Property</MenuItem>
                    <MenuItem value="storage">Storage Unit</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Timeline</InputLabel>
                  <Select
                    {...register('timeline')}
                    defaultValue=""
                    label="Timeline"
                  >
                    <MenuItem value="asap">As Soon As Possible</MenuItem>
                    <MenuItem value="1_month">Within 1 Month</MenuItem>
                    <MenuItem value="3_months">Within 3 Months</MenuItem>
                    <MenuItem value="flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message / Additional Details"
                  multiline
                  rows={4}
                  {...register('message')}
                  placeholder="Tell us about your situation, special requirements, or any questions you have..."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ cursor: 'pointer' }}
              >
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactForm;
